import React, { useCallback, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';

import {
	SummaryBlockComp,
	getBlockItems,
	setBlockItems,
} from '@components';

import {
	AddSummaryItemModal,
	DeleteSummaryItem,
	DeleteSummaryItemModal,
	UpdateSummaryItemModal
} from '@modals';

import { Agenda, SummaryBlock, SummaryBlockItem } from '@api';

import {
	useModal,
} from '@common';

import './SummaryList.scss';

interface SummaryProps {
	onUpdateSummaryData: (updatedSummaryData: SummaryBlock[]) => void,
	summaryBlocks: SummaryBlock[],
	agenda: Agenda,
	canEditResume: boolean,
	onHighlightihgChanged: (ids: string[]) => void,
}

function SummaryList(props: SummaryProps): JSX.Element {
	const {
		onUpdateSummaryData,
		summaryBlocks,
		canEditResume,
		agenda,
		onHighlightihgChanged,
	} = props;

	const {
		isModalOpen: isAddSummaryItemModalOpen,
		closeModal: closeAddSummaryItemModal,
		openModal: openAddSummaryItemModal,
	} = useModal();

	const {
		isModalOpen: isUpdateSummaryItemModalOpen,
		closeModal: closeUpdateSummaryItemModal,
		openModal: openUpdateSummaryItemModal,
	} = useModal();

	const {
		isModalOpen: isDeleteSummaryItemModalOpen,
		closeModal: closeDeleteSummaryItemModal,
		openModal: openDeleteSummaryItemModal,
	} = useModal();

	const [deletedSummaryItems, setDeletedSummaryItems] = useState<number>(0);

	const
		[selectedSummaryBlockIndex, setSelectedSummaryBlockIndex] = useState<number>(null),
		[selectedSummaryItemIndex, setSelectedSummaryItemIndex] = useState<number>(null),
		[selectedSummaryAgendaItemId, setSelectedSummaryAgendaItemId] = useState<string>(null)
	;

	const onSummaryDataUpdate = useCallback((updatedSummaryBlocks: SummaryBlock[]) => {
		onUpdateSummaryData(updatedSummaryBlocks);

		setSelectedSummaryBlockIndex(null);
		setSelectedSummaryItemIndex(null);
		setSelectedSummaryAgendaItemId(null);
	}, [onUpdateSummaryData, setSelectedSummaryBlockIndex, setSelectedSummaryItemIndex]);

	const onSummaryItemEdit = useCallback((summaryBlockIndex: number, summaryItemIndex: number, agendaItemId: string) => {
		setSelectedSummaryBlockIndex(summaryBlockIndex);
		setSelectedSummaryItemIndex(summaryItemIndex);
		setSelectedSummaryAgendaItemId(agendaItemId);
		openUpdateSummaryItemModal();
	}, [setSelectedSummaryBlockIndex, setSelectedSummaryItemIndex, openUpdateSummaryItemModal]);

	const onSummaryItemAdd = useCallback((summaryBlockIndex: number, summaryItemIndex: number, agendaItemId: string) => {
		setSelectedSummaryBlockIndex(summaryBlockIndex);
		setSelectedSummaryItemIndex(summaryItemIndex);
		setSelectedSummaryAgendaItemId(agendaItemId);
		openAddSummaryItemModal();
	}, [openAddSummaryItemModal, setSelectedSummaryBlockIndex, setSelectedSummaryItemIndex]);

	const onSummaryItemDeleted = useCallback((updatedSummaryBlocks: SummaryBlock[]) => {
		onSummaryDataUpdate(updatedSummaryBlocks);
		setDeletedSummaryItems(deletedSummaryItems + 1);
	}, [onSummaryDataUpdate, setDeletedSummaryItems, deletedSummaryItems]);

	const onSummaryItemDelete = useCallback((summaryBlockIndex: number, summaryItemIndex: number, agendaItemId: string) => {
		setSelectedSummaryBlockIndex(summaryBlockIndex);
		setSelectedSummaryItemIndex(summaryItemIndex);
		setSelectedSummaryAgendaItemId(agendaItemId);

		if (deletedSummaryItems < 2) {
			openDeleteSummaryItemModal();
		} else {
			const updatedSummaryBlocks = DeleteSummaryItem(summaryItemIndex, summaryBlockIndex, agendaItemId, summaryBlocks);
			onSummaryItemDeleted(updatedSummaryBlocks);
		}

	}, [openDeleteSummaryItemModal, deletedSummaryItems, summaryBlocks, onSummaryItemDeleted]);

	const onSummaryItemDrop = useCallback((summaryBlockIndex: number, summaryItem: SummaryBlockItem, summaryItemIndex: number, agendaItemId: string) => {
		const updatedBlock: SummaryBlock = cloneDeep(summaryBlocks[summaryBlockIndex]);
		updatedBlock.items.splice(summaryItemIndex, 0, summaryItem);
		summaryBlocks[summaryBlockIndex] = updatedBlock;
		summaryItem.agendaItemId = agendaItemId;
		onSummaryDataUpdate(summaryBlocks);

	}, [summaryBlocks, onSummaryDataUpdate]);

	const onSummaryItemReorderDrop = useCallback((oldBlock: SummaryBlock, oldIndex: number, newBlockIndex: number, newIdex: number, oldAgendaItemId: string, newAgendaItemId: string) => {
		const oldBlockIndex = summaryBlocks.findIndex(b => b.blockName == oldBlock.blockName);
		if (oldBlockIndex != newBlockIndex) {
			const initialBlock: SummaryBlock = cloneDeep(summaryBlocks[oldBlockIndex]);
			const targetBlock: SummaryBlock = cloneDeep(summaryBlocks[newBlockIndex]);

			const summaryItem = getBlockItems(initialBlock.__data, oldAgendaItemId)[oldIndex];

			let sourceItems = getBlockItems(initialBlock.__data, oldAgendaItemId);
			const targetItems = getBlockItems(targetBlock.__data, newAgendaItemId);

			targetItems.splice(newIdex, 0, summaryItem);
			sourceItems = sourceItems.filter((_, index) => index != oldIndex);

			setBlockItems(initialBlock.__data, oldAgendaItemId, sourceItems);
			setBlockItems(targetBlock.__data, newAgendaItemId, targetItems);

			summaryItem.agendaItemId = newAgendaItemId;

			summaryBlocks[oldBlockIndex] = initialBlock;
			summaryBlocks[newBlockIndex] = targetBlock;

			initialBlock.items = initialBlock.__data.getAllItems();
			targetBlock.items = targetBlock.__data.getAllItems();

		} else {
			const targetBlock: SummaryBlock = cloneDeep(summaryBlocks[newBlockIndex]);

			const summaryItem = getBlockItems(targetBlock.__data, oldAgendaItemId)[oldIndex];
			
			if (oldAgendaItemId == newAgendaItemId) {
				if (newIdex == oldIndex || newIdex == oldIndex + 1) {
					// do nothing
				} else {
					let items = getBlockItems(targetBlock.__data, oldAgendaItemId);
					
					if (newIdex < oldIndex) {
						items = items.filter((_, index) => index != oldIndex);
						items.splice(newIdex, 0, summaryItem);
					} else {
						items.splice(newIdex, 0, summaryItem);
						items = items.filter((_, index) => index != oldIndex);
					}
	
					setBlockItems(targetBlock.__data, oldAgendaItemId, items);
				}
			} else {
				let sourceItems = getBlockItems(targetBlock.__data, oldAgendaItemId);
				const targetItems = getBlockItems(targetBlock.__data, newAgendaItemId);

				targetItems.splice(newIdex, 0, summaryItem);
				sourceItems = sourceItems.filter((_, index) => index != oldIndex);

				setBlockItems(targetBlock.__data, oldAgendaItemId, sourceItems);
				setBlockItems(targetBlock.__data, newAgendaItemId, targetItems);
			}

			summaryItem.agendaItemId = newAgendaItemId;

			targetBlock.items = targetBlock.__data.getAllItems();
			summaryBlocks[newBlockIndex] = targetBlock;
		}
		onSummaryDataUpdate(summaryBlocks);

	}, [summaryBlocks, onSummaryDataUpdate]);

	const summaryItemsView = useMemo((): JSX.Element[] => {
		return summaryBlocks.map((summaryBlock, blockIndex) => (
			summaryBlock.items.length || canEditResume ? 
				<SummaryBlockComp
					onSummaryItemDelete={(summaryItemIndex, agendaItemId) => onSummaryItemDelete(blockIndex, summaryItemIndex, agendaItemId)}
					onSummaryItemEdit={(summaryItemIndex, agendaItemId) => onSummaryItemEdit(blockIndex, summaryItemIndex, agendaItemId)}
					onSummaryItemAdd={(summaryItemIndex, agendaItemId) => onSummaryItemAdd(blockIndex, summaryItemIndex, agendaItemId)}
					onSummaryItemDrop={(summaryItem, summaryItemIndex, agendaItemId) => onSummaryItemDrop(blockIndex, summaryItem, summaryItemIndex, agendaItemId)}
					onSummaryItemReorderDrop={(oldBlock, oldIdex, newIndex, oldAgendaItemId, newAgendaItemId) => onSummaryItemReorderDrop(oldBlock, oldIdex, blockIndex, newIndex, oldAgendaItemId, newAgendaItemId)}
					onHighlightihgChanged={onHighlightihgChanged}
					summaryBlock={summaryBlock}
					agenda={agenda}
					canEdit={canEditResume}
					key={blockIndex}
				/>
				: null
			
		));
	}, [summaryBlocks, agenda, onSummaryItemAdd, onSummaryItemDelete, onSummaryItemEdit, onSummaryItemReorderDrop, onSummaryItemDrop,
		onHighlightihgChanged, canEditResume]);

	const addSummaryItemModalView = useMemo((): JSX.Element => {
		return isAddSummaryItemModalOpen && (
			<AddSummaryItemModal
				closeModal={closeAddSummaryItemModal}
				summaryItemIndex={selectedSummaryItemIndex}
				summaryBlockIndex={selectedSummaryBlockIndex}
				summaryAgendaItemId={selectedSummaryAgendaItemId}
				summaryBlocks={summaryBlocks}
				agenda={agenda}
				onSubmit={onSummaryDataUpdate}
			/>
		);
	}, [summaryBlocks, onSummaryDataUpdate, selectedSummaryBlockIndex, closeAddSummaryItemModal,
		isAddSummaryItemModalOpen, selectedSummaryItemIndex, selectedSummaryAgendaItemId,
		agenda]);

	const updateSummaryItemModalView = useMemo((): JSX.Element => {
		return isUpdateSummaryItemModalOpen && (
			<UpdateSummaryItemModal
				closeModal={closeUpdateSummaryItemModal}
				summaryItemIndex={selectedSummaryItemIndex}
				summaryBlockIndex={selectedSummaryBlockIndex}
				summaryAgendaItemId={selectedSummaryAgendaItemId}
				summaryBlocks={summaryBlocks}
				agenda={agenda}
				onSubmit={onSummaryDataUpdate}
			/>
		);
	}, [summaryBlocks, onSummaryDataUpdate, selectedSummaryBlockIndex, selectedSummaryItemIndex, 
		closeUpdateSummaryItemModal, isUpdateSummaryItemModalOpen, selectedSummaryAgendaItemId,
		agenda]);

	const deleteSummaryItemModalView = useMemo((): JSX.Element => {
		return isDeleteSummaryItemModalOpen && (
			<DeleteSummaryItemModal
				closeModal={closeDeleteSummaryItemModal}
				summaryItemIndex={selectedSummaryItemIndex}
				summaryBlockIndex={selectedSummaryBlockIndex}
				summaryAgendaItemId={selectedSummaryAgendaItemId}
				summaryBlocks={summaryBlocks}
				onSubmit={onSummaryItemDeleted}
			/>
		);
	}, [selectedSummaryItemIndex, selectedSummaryBlockIndex, isDeleteSummaryItemModalOpen,
		closeDeleteSummaryItemModal, summaryBlocks, selectedSummaryAgendaItemId, onSummaryItemDeleted]);

	return (
		<div className="summary-box">
			<div className="summary-block-list">
				{summaryItemsView}
			</div>
			{addSummaryItemModalView}
			{updateSummaryItemModalView}
			{deleteSummaryItemModalView}
		</div>
	);
}

export {SummaryList};